import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Plus, X, MapPin, Layers } from 'lucide-react';
import whsService from '../../../services/whs';
import type {
  RiskAssessmentListItem, RiskAssessmentStatus, RiskAssessmentType, RiskMatrix as RiskMatrixConfig,
} from '../../../types/whs';
import { levelForScore } from '../../../components/whs/RiskMatrix';
import {
  RiskBadge, Badge, RA_TYPE_LABEL, RA_STATUS_LABEL, RA_STATUS_CLASS,
} from '../../../components/whs/whsConstants';
import WhsGate from '../../../components/whs/WhsGate';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const inputClass =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const RA_TYPE_KEYS = Object.keys(RA_TYPE_LABEL) as RiskAssessmentType[];
const RA_STATUS_KEYS = Object.keys(RA_STATUS_LABEL) as RiskAssessmentStatus[];

interface NewForm {
  title: string;
  assessment_type: RiskAssessmentType;
  reference_code: string;
  activity: string;
  location: string;
  assessment_date: string;
  review_date: string;
}

const EMPTY_FORM: NewForm = {
  title: '',
  assessment_type: 'generic',
  reference_code: '',
  activity: '',
  location: '',
  assessment_date: '',
  review_date: '',
};

function RiskAssessmentsInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const [items, setItems] = useState<RiskAssessmentListItem[]>([]);
  const [matrix, setMatrix] = useState<RiskMatrixConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<RiskAssessmentStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<RiskAssessmentType | ''>('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<NewForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listRiskAssessments({
      status: statusFilter || undefined,
      assessment_type: typeFilter || undefined,
    });
    if (res.success) setItems(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    whsService.getRiskMatrix().then((res) => {
      if (active && res.success) setMatrix(res.data);
    });
    return () => { active = false; };
  }, []);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setFormError('A title is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const res = await whsService.createRiskAssessment({
      title: form.title.trim(),
      assessment_type: form.assessment_type,
      reference_code: form.reference_code.trim() || null,
      activity: form.activity.trim() || null,
      location: form.location.trim() || null,
      assessment_date: form.assessment_date || null,
      review_date: form.review_date || null,
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      navigate(`/hr/whs/risk-assessments/${res.data.id}`);
    } else {
      setFormError(res.error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Risk Assessments"
        subtitle="JSAs, SWMS & risk registers with likelihood × consequence scoring"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <Button size="sm" onClick={openModal}>
            <Plus className="h-4 w-4" /> New risk assessment
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RiskAssessmentStatus | '')}
            className={`${inputClass} max-w-[220px]`}
          >
            <option value="">All statuses</option>
            {RA_STATUS_KEYS.map((s) => (
              <option key={s} value={s}>{RA_STATUS_LABEL[s]}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as RiskAssessmentType | '')}
            className={`${inputClass} max-w-[300px]`}
          >
            <option value="">All types</option>
            {RA_TYPE_KEYS.map((t) => (
              <option key={t} value={t}>{RA_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>

        {/* List */}
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
          {loading ? (
            <p className="p-6 text-sm text-neutral-400">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-neutral-400">No risk assessments found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Assessment</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Highest residual</th>
                    <th className="px-4 py-3">Review due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {items.map((ra) => (
                    <tr
                      key={ra.id}
                      onClick={() => navigate(`/hr/whs/risk-assessments/${ra.id}`)}
                      className="cursor-pointer hover:bg-neutral-50 text-sm"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-neutral-900">{ra.title}</div>
                        {ra.reference_code && (
                          <div className="text-xs text-neutral-500">{ra.reference_code}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{RA_TYPE_LABEL[ra.assessment_type]}</td>
                      <td className="px-4 py-3">
                        <Badge label={RA_STATUS_LABEL[ra.status]} className={RA_STATUS_CLASS[ra.status]} />
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-neutral-400" />
                          {ra.farm_name || ra.location || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        <span className="inline-flex items-center gap-1">
                          <Layers className="h-3.5 w-3.5 text-neutral-400" />
                          {ra.item_count}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {ra.max_residual_score != null && matrix
                          ? <RiskBadge level={levelForScore(ra.max_residual_score, matrix)} />
                          : <RiskBadge level={null} />}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        {ra.review_date ? formatLocalDateInTz(ra.review_date, orgTz) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New risk assessment modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">New risk assessment</h2>
              <button onClick={() => setModalOpen(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  className={inputClass}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Orchard mowing operations"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Assessment type</label>
                <select
                  className={inputClass}
                  value={form.assessment_type}
                  onChange={(e) => setForm({ ...form, assessment_type: e.target.value as RiskAssessmentType })}
                >
                  {RA_TYPE_KEYS.map((t) => (
                    <option key={t} value={t}>{RA_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reference code</label>
                <input
                  className={inputClass}
                  value={form.reference_code}
                  onChange={(e) => setForm({ ...form, reference_code: e.target.value })}
                  placeholder="e.g. RA-2026-012"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Activity / task</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={form.activity}
                  onChange={(e) => setForm({ ...form, activity: e.target.value })}
                  placeholder="Describe the activity being assessed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
                <input
                  className={inputClass}
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Assessment date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.assessment_date}
                    onChange={(e) => setForm({ ...form, assessment_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Review date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.review_date}
                    onChange={(e) => setForm({ ...form, review_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} isLoading={saving}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsRiskAssessments() {
  return <WhsGate><RiskAssessmentsInner /></WhsGate>;
}
